import moment from "moment";
import { FC, useMemo } from "react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import SubscribeIcon from "./assets/log-in.svg?react";
import UnsubscribeIcon from "./assets/log-out.svg?react";
import UpdateIcon from "./assets/edit-icon.svg?react";
import { TimeLineItem } from "shared/components/TimeLine";
import {
  AudiLog,
  AuditableType,
  LogChangesType,
  LogEvent,
  UpdateFields,
  UpdateTitles
} from "views/rent-reporting-id/types";
import { ArrowRight } from "untitledui-js-base";
import { useAuthContext } from "providers/Auth";

type Props = {
  log: AudiLog;
  isLast?: boolean;
  residentName?: string;
};

const getReadableChanges = (changes: LogChangesType) => {
  return Object.entries(changes).map(([key, values]) => {
    const readableTitle = UpdateTitles[key as UpdateFields] || key;
    return {
      title: readableTitle,
      values
    };
  });
};

export const LogDescription: FC<Props> = ({ log, isLast, residentName }) => {
  const { auth } = useAuthContext();
  const partnerName = auth?.partner.name;
  const partnerEmail = auth?.partner.email;
  const logInfo = useMemo(() => {
    if (
      log.event === LogEvent.CREATE &&
      log.auditable_type === AuditableType.BOOM_CUSTOMER
    ) {
      const enrollmentMethod = log.changes?.enrollment_method?.[0] as
        | string
        | undefined;
      const subscriberName = residentName || "Resident";
      const methodLabel = (enrollmentMethod || "").trim();
      const actorName = (log.actor.name || "").trim();
      const actorEmail = (log.actor.email || "").trim();
      const actorLogin =
        actorName && actorEmail
          ? `${actorName} (${actorEmail})`
          : actorName || actorEmail || "Unknown user";
      const byActorText =
        methodLabel !== "via auto enrollment" ? `by ${actorLogin}` : "";

      return {
        title: `${subscriberName} subscribed ${methodLabel} ${byActorText}`,
        icon: <SubscribeIcon className="w-4 h-4 text-success-700" />
      };
    }

    if (log.event === LogEvent.UPDATE) {
      if (log.auditable_type === AuditableType.BOOM_CUSTOMER) {
        const changes = log.changes || {};
        const formattedChanges = getReadableChanges(changes);
        return {
          title: `${log.actor.name || ""} changed `,
          icon: <UpdateIcon className="w-4 h-4 text-content-500" />,
          changes: formattedChanges
        };
      }
    }

    if (log.event === LogEvent.MANUAL_VERIFY)
      return {
        title: `${log.actor.name || "Auto"} reported rent`,
        icon: <SuccessIcon className="w-4 h-4 text-success-700" />,
        changes: [
          {
            values: [
              `${moment(log.auditable_entity.for_month).format("MMM YYYY")}`,
              log.auditable_entity.months_delinquency > 0
                ? `${log.auditable_entity.months_delinquency} Month Late`
                : "Paid On-Time"
            ]
          }
        ]
      };

    if (log.event === LogEvent.REQUIRE_MANUAL_VERIFICATION)
      return {
        title: `${log.actor.name || "Auto"} reported rent`,
        icon: <SuccessIcon className="w-4 h-4 text-success-700" />,
        changes: [
          {
            values: [
              `${moment(log.auditable_entity.for_month).format("MMM YYYY")}`,
              "No data"
            ]
          }
        ]
      };

    if (log.auditable_type === AuditableType.BOOM_ENROLLMENT) {
      if (log.event === LogEvent.RESIDENT_OPTED_OUT)
        return {
          title: `Renter ${residentName} opted out using email link`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.AUTO_OPTED_OUT)
        return {
          title: `Renter ${residentName} was auto opted out by Boom`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.RESIDENT_OPTED_IN)
        return {
          title: `Renter ${residentName} opted in using email link`,
          icon: <SuccessIcon className="w-4 h-4 text-success-700" />
        };
      if (log.event === LogEvent.ADMIN_SUBMIT_ENROLLMENT)
        return {
          title: `Enrollment submitted by Boom staff`,
          icon: <SuccessIcon className="w-4 h-4 text-success-700" />
        };
      if (log.event === LogEvent.APPROVE)
        return {
          title: `Enrollment approved by Boom`,
          icon: <SuccessIcon className="w-4 h-4 text-success-700" />
        };
      if (log.event === LogEvent.SIGN_IN_OPTED_IN)
        return {
          title: `Renter ${residentName} opted in by logging in / registering`,
          icon: <SuccessIcon className="w-4 h-4 text-success-700" />
        };
      if (log.event === LogEvent.UNENROLL_BY_PARTNER)
        return {
          title: `Renter ${residentName} has been unenrolled by partner ${partnerName} (${partnerEmail})`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.UNENROLL_ONGOING)
        // legacy event, we don't know the reason or unenrollment source.
        return {
          title: `Renter ${residentName} has been unenrolled`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.UNENROLL_BY_ADMIN)
        return {
          title: `Renter ${residentName} has been unenrolled by Boom staff`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.UNENROLL_BY_45_DAYS_PENDING_POLICY)
        return {
          title: `Renter ${residentName} has been unenrolled due to 45 days pending policy by Boom`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (log.event === LogEvent.UNENROLL_MOVE_OUT)
        return {
          title: `Renter ${residentName} has been unenrolled due to move out`,
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
      if (
        log.event === LogEvent.REVERSE_UNENROLL ||
        log.event === LogEvent.REVERSE_OPT_OUT ||
        log.event === LogEvent.RECONSIDER
      )
        return {
          title: `Renter ${residentName} has been re-enrolled by Boom staff`,
          icon: <SuccessIcon className="w-4 h-4 text-success-700" />
        };
    }

    return null;
  }, [log, residentName]);

  if (!logInfo) return null;

  return (
    <TimeLineItem
      wrapperClassName="items-center pt-12 flex"
      isLast={isLast}
      bullet={
        <div className="border border-alpha-100 translate-x-[-16px] p-2 rounded-full bg-background-1000">
          {logInfo.icon}
        </div>
      }
      childrenClassName="flex-1 w-full"
    >
      {logInfo.changes?.length ? (
        <>
          {logInfo.changes.map((change, index) => (
            <div key={index} className="mb-2">
              <p className="text-content-1000 font-medium text-base mb-2">
                {logInfo.title} {change?.title}
              </p>
              <div className="flex gap-4 bg-alpha-40 py-3 px-4 rounded-lg border border-alpha-100 items-center">
                <p className="font-medium text-xs text-content-400">
                  {change.values[0] || "No data"}
                </p>
                <ArrowRight className="text-content-100" size="16" />
                <p className="font-medium text-xs text-content-400">
                  {change.values[1]}
                </p>
              </div>
            </div>
          ))}
          <p className="text-xs text-content-300 font-medium">
            {moment(log.created_at).format("MMM D, YYYY [at] h:mm A")}
          </p>
        </>
      ) : (
        <>
          <p className="text-content-1000 font-medium text-base mb-1">
            {logInfo.title}
          </p>
          <p className="text-xs text-content-300 font-medium">
            {moment(log.created_at).format("MMM D, YYYY [at] h:mm A")}
          </p>
        </>
      )}
    </TimeLineItem>
  );
};
